import { Product } from '../types';
import gorillaLogo from '../assets/images/gorilla/gorilla-product.svg';
import ipad1 from '../assets/images/gorilla/ipad1.png';
import ipad2 from '../assets/images/gorilla/ipad2.png';
import iphone1 from '../assets/images/gorilla/iphone1.png';
import iphone2 from '../assets/images/gorilla/iphone2.png';
import ravenLogo from '../assets/images/raven/raven-product.svg';
import ravenIpad1 from '../assets/images/raven/ipad1.png';
import ravenIpad2 from '../assets/images/raven/ipad2.png';
import ravenIpad3 from '../assets/images/raven/ipad3.png';
import ravenIphone1 from '../assets/images/raven/iphone1.png';
import ravenIphone2 from '../assets/images/raven/iphone2.png';
import ravenIphone3 from '../assets/images/raven/iphone3.png';
import chordpondLogo from '../assets/images/chordpond/chordpond-product.png';
import chordpondIpad1 from '../assets/images/chordpond/ipad1.png';
import chordpondIpad2 from '../assets/images/chordpond/ipad2.png';
import chordpondIpad3 from '../assets/images/chordpond/ipad3.png';
import chordpondIpad4 from '../assets/images/chordpond/ipad4.png';
import chordpondIpad5 from '../assets/images/chordpond/ipad5.png';
import chordpondIpad6 from '../assets/images/chordpond/ipad6.png';
import chordpondIphone1 from '../assets/images/chordpond/iphone1.png';
import chordpondIphone2 from '../assets/images/chordpond/iphone2.png';
import chordpondIphone3 from '../assets/images/chordpond/iphone3.png';
import chordpondIphone4 from '../assets/images/chordpond/iphone4.png';
import chordpondIphone5 from '../assets/images/chordpond/iphone5.png';
import chordpondIphone6 from '../assets/images/chordpond/iphone6.png';

export const products: Product[] = [
  {
    id: 'gorilla',
    name: 'GORILLA',
    tagline: 'Bold, punchy analog-style subtractive synth.',
    description:
      'A focused dual-oscillator instrument with sub, ladder filter, dual ADSR, and AUv3 support. Designed for fast sound design and expressive play.',
    logo: {
      url: gorillaLogo,
      alt: 'GORILLA Product Mark',
    },
    features: [
      'Duophonic mode + glide',
      'Ladder filter + dual ADSR',
      'LFO for rate/pitch/filter/PWM',
      'AUv3, CoreMIDI, Bluetooth MIDI',
    ],
    screenshots: [
      {
        url: ipad1,
        alt: 'GORILLA iPad interface - main view',
        width: 2048,
        height: 2732,
      },
      {
        url: ipad2,
        alt: 'GORILLA iPad interface - visualiser and EQ view',
        width: 2048,
        height: 2732,
      },
      {
        url: iphone1,
        alt: 'GORILLA iPhone interface - performance view',
        width: 1170,
        height: 2532,
      },
      {
        url: iphone2,
        alt: 'GORILLA iPhone interface - Keyboard view',
        width: 1170,
        height: 2532,
      },
    ],
    appStoreUrl: 'https://apps.apple.com/us/app/gorilla-synth/id6756503672',
    videos: [
      {
        url: 'https://www.youtube.com/watch?v=9B1kyQnuDQI',
        title: 'Jam with v1.2',
        description: 'Community jam session showcasing GORILLA v1.2',
      },
      {
        url: 'https://www.youtube.com/watch?v=vsnQh9mPpoI',
        title: 'Demo Session v1.0',
        description: 'Demo session with the first version of GORILLA',
      },
    ],
    versionHistory: [
      {
        version: '1.4',
        changes: [
          '11 new factory presets across sub, analog bass, house, and pluck categories',
          'Full MIDI CC support — 24 parameters mappable to external controllers',
          'MIDI CC mappings view accessible from the Info screen',
          'macOS / Logic Pro support via Mac Catalyst (AUv3, full automation, macOS 12+)',
        ],
      },
      {
        version: '1.3',
        changes: [
          '10 new factory presets spanning plucks, leads, subs, and house basses',
          'Keyboard tracking — filter cutoff follows pitch for brighter highs (toggle per preset)',
          'New logarithmic filter cutoff AUv3 parameter for natural automation feel',
        ],
      },
      {
        version: '1.2',
        changes: [
          'Improved knob responsiveness and precision across all devices',
          'Smoother, more musical filter behavior at high resonance',
          'Fixed rare filter dropouts at extreme cutoff/resonance settings',
          'Full AUv3 interface now available on iPhone (not only parameters)',
          '6 new presets added',
          'General stability and performance improvements',
        ],
        note: 'Keyboard tracking planned for v1.3',
      },
      {
        version: '1.1',
        changes: [
          'Full synth parameter access on iPhone and iPad',
          'New swipe-based phone UI with horizontal panels',
          'AUv3 state now properly saves and restores in host apps',
          'Full AUv3 parameter exposure for automation and MIDI control',
          'Fixed master volume, pitch calibration, and preset loading issues',
          'Improved preset QR scanning and sharing',
          'New LFO waveform options',
          'Added new factory presets + first community preset',
        ],
        note: 'Thanks to the iOS music community for the invaluable feedback!',
      },
    ],
  },
  {
    id: 'raven',
    name: 'RAVEN',
    tagline: 'Deep, drawable synth for evolving textures and sculpted sound.',
    description:
      'An expressive wavetable-morphing synthesizer with drawable waveforms, real-time morphing, dual LFOs with custom modulation routing, and a full effects chain. Built for pads, sound design, and live performance.',
    logo: {
      url: ravenLogo,
      alt: 'RAVEN Product Mark',
    },
    screenshots: [
      {
        url: ravenIpad1,
        alt: 'RAVEN iPad interface - main synth view with LFO 1',
        width: 2752,
        height: 2064,
      },
      {
        url: ravenIpad2,
        alt: 'RAVEN iPad interface - new LFO 2 with custom waveform and modulation slots',
        width: 2752,
        height: 2064,
      },
      {
        url: ravenIpad3,
        alt: 'RAVEN iPad interface - EQ visualizer',
        width: 2752,
        height: 2064,
      },
      {
        url: ravenIphone1,
        alt: 'RAVEN iPhone interface - main synth view',
        width: 1320,
        height: 2868,
      },
      {
        url: ravenIphone2,
        alt: 'RAVEN iPhone interface - LFO and modulation',
        width: 1320,
        height: 2868,
      },
      {
        url: ravenIphone3,
        alt: 'RAVEN iPhone interface - FX and delay controls',
        width: 1320,
        height: 2868,
      },
    ],
    features: [
      '16-voice polyphonic engine with dual drawable oscillators + wavetable morphing',
      'Ladder filter (12/24dB, LP/BP/HP) + graph-based dual ADSR',
      'Dual LFOs with drawable waveforms, fade-in (up to 20s), and flexible modulation slots',
      'LFO destinations include pitch, filter, PWM, morph, amp, and mod wheel',
      'FX chain: chorus → distortion → delay → reverb',
      '12-band parametric EQ',
      'AUv3 instrument, CoreMIDI, Bluetooth MIDI',
    ],
    appStoreUrl: 'https://apps.apple.com/us/app/raven-synth/id6761521717',
    videos: [
      {
        url: 'https://www.youtube.com/watch?v=I14KFG4MX-w',
        title: 'RAVEN Synth Video',
        description: 'Community video showcasing RAVEN Synth.',
      },
      {
        url: 'https://www.youtube.com/watch?v=lT9zRU5jMJA&start=2872',
        title: 'Exploring Raven Synth by Field Tone Audio',
        description: 'In-depth 2-hour livestream session exploring RAVEN Synth.',
      },
      {
        url: 'https://www.youtube.com/watch?v=-KJZJH4bTNw',
        title: 'RAVEN Synth DEMO - 3 layers of factory presets',
        description:
          'Testing v1.0 of the new RAVEN Synth. 3 layered factory presets inside RAVEN — GarageBand session, stock drums only.',
      },
    ],
    versionHistory: [
      {
        version: '1.7',
        title: 'The one that runs lighter.',
        changes: [
          'Lower CPU usage across AUv3',
          'Added animations toggle (disabled by default in AUv3) for better multi-instance performance and smoother controls',
          'Fixed UI lag and heavy preset operations',
          'Reduced overall CPU and UI load',
        ],
      },
      {
        version: '1.6',
        title: 'The small one.',
        changes: [
          'Added LFO2 Unipolar mode (UNI)',
        ],
      },
      {
        version: '1.3',
        title: 'The one that widens the sound palette.',
        changes: [
          'Added LFO 2 with flexible routing and modulation slots',
          'Added LFO fade-in (up to 20s) for evolving textures',
          'Extended delay times for spacious echoes and tails',
          'Added 7 new presets showcasing movement and modulation',
          'Reworked voice retrigger behavior',
          'UI refinements and layout improvements across devices',
        ],
      },
      {
        version: '1.2',
        title: 'The one with no clicks or pops.',
        changes: [
          'Fixed clicks and pops when playing notes and chords',
          'Improved voice handling for smoother sound',
          'General audio engine improvements',
        ],
      },
      {
        version: '1.1',
        changes: [
          'Better gain staging + output limiter (no more unexpected loud spikes)',
          'Polyphonic volume balancing — chords feel more natural',
          'Logarithmic LFO rate for precise slow modulation',
          'Extended ADSR ranges (up to 8 seconds)',
          'Double-tap any control to reset it',
          'New randomize button for quick inspiration',
          'Adjustable audio buffer size (standalone)',
          'Smoother visualizations (30fps)',
          'Noise is now its own mixer channel',
          'OSC2 gets coarse tuning (±24 semitones)',
          'Improved keyboard tracking (now accurate)',
          'More flexible LFO routing',
          'Fixed clicks/pops when changing presets',
          'Fixed envelope retrigger issues',
          'Fixed OSC2 sync drift bug',
          'Fixed SPLIT mode envelope behavior',
          'Fixed UI inconsistencies (ADSR, playheads, etc.)',
          'New master volume + output meter (iPad)',
          'Factory presets rebalanced for consistent loudness',
          'Removed overly loud patches',
          'User preset bank now starts empty',
          'New preset pack import/export (.ravenpack)',
        ],
        note: 'Big stability & sound update — thanks for all the feedback on 1.0!',
      },
      {
        version: '0.3',
        changes: [
          'AUv3 host keyboard space toggle — "KB" button for hosts without on-screen keyboards',
          'Small display support for 9.7" iPads — scrollable grid with adaptive row heights',
          'UI performance overhaul — decoupled heavy Canvas views from engine updates, reduced published properties',
          'Fixed LFO filter modulation running through envelope amount',
          'Fixed filter deadspot at high resonance + upper cutoff range',
        ],
      },
      {
        version: '0.2',
        changes: [
          'AUv3 plugin loading and GarageBand scroll fixes',
          'iPhone AUv3 — full interface now renders correctly on phone',
          'On-screen keyboard no longer blocks bottom UI controls',
          'LFO section toggle and gain dial binding fixes',
          'Morph oscilloscope now reads directly from OSC1/OSC2 wavetables',
          'Output volume raised for louder, fuller mix bus',
        ],
      },
      {
        version: '0.1',
        changes: [
          'Dual drawable oscillators with wavetable morphing and phase-locked crossfading',
          'Utility oscillator — sub, ring mod, FM, noise, and harmonics injection',
          'Waveform drawing, smoothing, and explode (wavefold distortion) tools',
          'Ladder filter with 12/24dB slopes, LP/BP/HP modes, and resonance compensation',
          'Graph-based filter and amp envelopes with real-time playback animation',
          'LFO core with 4 waveforms + custom drawable wavetable',
          'Voice unison and stereo spread (1/2/4 detuned voices)',
          '56 factory presets with QR code sharing',
          'Aftertouch routing, keyboard tracking, portamento, pitch bend',
          'iPhone + iPad adaptive layouts',
        ],
      },
    ],
  },
  {
    id: 'chordpond',
    name: 'CHORDPOND',
    tagline: 'Build chord progressions, shape their harmony, and make them move.',
    description:
      'Explore 200+ chord progressions or sketch your own paths through chord tiles. Preview, transpose, and reshape harmony and rhythm, then add a progression to a scene and play it through the built-in Frog Synth or route MIDI to hosted AUv3 instruments, external synths, and your DAW. ChordPond also gives you fast path and scene editing, custom motifs and basslines, Ableton Link sync, and AUv3 host integration.',
    logo: {
      url: chordpondLogo,
      alt: 'CHORDPOND Product Mark',
    },
    screenshots: [
      {
        url: chordpondIpad1,
        alt: 'Sketch chords as paths — connect chord tiles into a progression',
        width: 2732,
        height: 2048,
        device: 'ipad',
      },
      {
        url: chordpondIpad2,
        alt: 'Shape every chord — voicings, bass, tensions, and octave',
        width: 2732,
        height: 2048,
        device: 'ipad',
      },
      {
        url: chordpondIpad3,
        alt: 'Edit motion inside each tile — notes, velocity, micro-timing, ratchets',
        width: 2732,
        height: 2048,
        device: 'ipad',
      },
      {
        url: chordpondIpad4,
        alt: 'Play with human feel — timing, velocity, probability, and octave humanization',
        width: 2732,
        height: 2048,
        device: 'ipad',
      },
      {
        url: chordpondIpad5,
        alt: 'Change key in one tap — switch keys and scales while the progression stays playable',
        width: 2732,
        height: 2048,
        device: 'ipad',
      },
      {
        url: chordpondIpad6,
        alt: 'Send MIDI anywhere — drive external instruments, synth apps, and AUv3 hosts',
        width: 2732,
        height: 2048,
        device: 'ipad',
      },
      {
        url: chordpondIphone1,
        alt: 'Sketch chords as paths on iPhone — connect chord tiles into a progression',
        width: 2778,
        height: 1284,
        device: 'iphone',
      },
      {
        url: chordpondIphone2,
        alt: 'Shape every chord on iPhone — voicings, bass, tensions, and octave',
        width: 2778,
        height: 1284,
        device: 'iphone',
      },
      {
        url: chordpondIphone3,
        alt: 'Edit motion inside each tile on iPhone — notes, velocity, micro-timing, ratchets',
        width: 2778,
        height: 1284,
        device: 'iphone',
      },
      {
        url: chordpondIphone4,
        alt: 'Play with human feel on iPhone — timing, velocity, probability, and octave',
        width: 2778,
        height: 1284,
        device: 'iphone',
      },
      {
        url: chordpondIphone5,
        alt: 'Change key in one tap on iPhone — switch keys and scales instantly',
        width: 2778,
        height: 1284,
        device: 'iphone',
      },
      {
        url: chordpondIphone6,
        alt: 'Send MIDI anywhere from iPhone — to external instruments, synth apps, and AUv3 hosts',
        width: 2812,
        height: 1284,
        device: 'iphone',
      },
    ],
    features: [
      '200+ chord progressions — search by mood, length, complexity, or category; save favorites and revisit recent finds',
      'Preview and transpose progressions, find similar ideas, and vary their harmony or pad lengths',
      'Add a progression to the current scene or a new scene on your chosen channel',
      'Apply pad playback settings to a path or scene in one step, with undo',
      'Visual chord tiles with major, minor, and dominant sevenths, extended harmony, voicings, and inversions',
      'Built-in Frog Synth with Frog Init, Warm Poly, and Soft Pad patches, sub and square oscillators, PWM, and full ADSR',
      'Dedicated bass playback per chord pad with 14 factory bass patterns',
      'Visual motif browser and editor — create, audition, save, and reuse your own motifs and basslines',
      'Smooth, pinch-to-zoom Scale Grid with continuous note navigation across octaves',
      '20+ scales and flat keys, including D♭, E♭, G♭, A♭, and B♭',
      'Multiple play modes per tile — Hold, Stutter, Roll/Arp, Motif, Random',
      'Paths and scenes for full song arrangements, with scene deletion and undo',
      'Ableton Link sync for standalone tempo, beat phase, and transport',
      'AUv3 MIDI processor with host transport + tempo and project restore',
      'Hosted instrument rack with AUv3 loading and per-channel FX chains',
      'Standalone background audio keep-alive for external MIDI rigs',
      'iPad app runs on macOS via Mac Catalyst — standalone and AUv3 in Logic Pro',
      'CoreMIDI output to external hardware synths and other apps',
      'Shared project library between standalone and AUv3',
    ],
    appStoreUrl: 'https://apps.apple.com/us/app/chordpond/id6769392239',
    videosTitle: 'WATCH CHORDPOND',
    videosDescription:
      'See CHORDPOND in action, then learn how to connect it to Synth One, AUM, Logic Pro for iPad, and other iOS music setups.',
    videos: [
      {
        url: 'https://www.youtube.com/watch?v=FhDFJFB_b1I',
        title: 'ChordPond v1.9 | Memory Reboot - VØJ & Narvent',
        description:
          'A Memory Reboot cover built in ChordPond v1.9, showing off the new dedicated bass playback, factory bass patterns, and the expanded Frog Synth.',
      },
      {
        url: 'https://youtu.be/i_YO7Bw8tf4',
        title: 'ChordPond v1.7 | Better Off Alone (Lullaby) + New Scale Grid',
        description:
          'A soft, lullaby-inspired Better Off Alone arrangement showcasing smooth Scale Grid scrolling, pinch-to-zoom, and continuous note navigation across octaves.',
      },
      {
        url: 'https://youtu.be/-lli0982hek',
        title: 'Getting Started with CHORDPOND for iOS',
        description:
          'Set up standalone mode with AudioKit Synth One, use CHORDPOND as an AUv3 MIDI processor in AUM, and route it in Logic Pro for iPad.',
      },
      {
        url: 'https://www.youtube.com/watch?v=X-GyKJHqV9o',
        title: 'Introduction to CHORDPOND',
        description: 'The best introduction to CHORDPOND — what it is and how it works.',
      },
      {
        url: 'https://youtu.be/aDNQiXKo2E8',
        title: 'CHORDPOND Demo Video',
        description: 'Official video showcasing CHORDPOND in action.',
      },
    ],
    versionHistory: [
      {
        version: '2.2',
        title: 'The one that knows the difference between A7 and Amaj7.',
        changes: [
          'Choose major, minor, or dominant seventh chords with labels that reflect the resulting harmony',
          'Refined controls for 7th, 9th, 11th, and 13th extensions in a reorganized pad editor',
          'Added flat keys: D♭, E♭, G♭, A♭, and B♭',
          'Delete scenes with confirmation and undo',
        ],
      },
      {
        version: '2.1',
        title: 'The one that saves a lot of tapping.',
        changes: [
          'Apply to Path copies a pad’s playback settings, velocity, gate, and mode-specific settings across the path',
          'Apply to Scene brings those settings to the wider scene when you choose to',
          'Bulk edits support undo and preserve individual chords, painted notes, pad lengths, and routing',
        ],
      },
      {
        version: '2.0',
        title: 'The one with a big progression library.',
        changes: [
          'Explore 200+ chord progressions with search and filters for mood, length, complexity, and category',
          'Save favorites, revisit recently viewed progressions, and preview ideas before adding them',
          'Transpose progressions to other keys and find similar progressions',
          'Explore harmony variations: simpler, darker, more tension, more colourful, and unexpected',
          'Create rhythm variations that change the actual pad lengths',
          'Add a progression to the current scene or a new scene on your chosen channel',
          'Added Frog Init, Warm Poly, and Soft Pad patches for Frog Synth',
          'Polished the iPhone and browser interface',
        ],
      },
      {
        version: '1.9',
        title: 'The one with basslines.',
        changes: [
          'Added dedicated bass playback for chord pads',
          'Added 14 factory bass patterns',
          'Create, audition, save, and reuse your own basslines',
          'Expanded Frog Synth with a sub oscillator and full ADSR envelope',
          'Refreshed the Frog Synth interface',
        ],
      },
      {
        version: '1.8',
        title: 'The one with custom motifs.',
        changes: [
          'Added visual motif browser and editor',
          'Create, audition, save, and reuse your own motifs',
          'Added 3 new built-in motifs',
          'Added square oscillator with pulse width control to Frog Synth',
        ],
      },
      {
        version: '1.7',
        title: 'The one with a better Scale Grid.',
        changes: [
          'Improvement: Smooth scrolling and pinch-to-zoom in the Scale Grid',
          'Improvement: Continuous note navigation across octaves',
          'Fix: Fixed canvas layout when renaming projects on iPad',
          'Fix: Fixed motifs playing beyond the end of a chord',
        ],
      },
      {
        version: '1.6',
        title: 'FX chains & chord workflow.',
        changes: [
          'Per-channel FX chains for hosted instruments, with add/remove, reorder, enable state, and AU state persistence',
          'Seamless FX bypass using AU bypass so sustained audio keeps flowing when effects toggle',
          'Chord workflow upgrades: faster navigation, advanced chord picker, notes-only mute, and grid shortcuts',
          'Grid clarity improvements so chord tones are easier to read against painted notes',
          'FX chain and Add FX picker now adapt better in Morning/Day solid background mode',
          'Regression coverage for rack and playback/editor invariants',
        ],
      },
      {
        version: '1.5.1',
        title: 'Solid background fixes.',
        changes: [
          'Removed the sticky dark toolbar cap on compact phones when solid backgrounds are enabled',
          'Instrument rack now renders over a full-screen themed backdrop with a legible header',
          'Instrument picker uses dark text and controls in Morning/Day solid mode for better readability',
          'Project picker chrome and empty-canvas guidance now adapt to dark text on light solid backgrounds',
          'Stop mode uses a plain dialog trigger on iPhone, while scale uses the inline chip strip',
        ],
      },
      {
        version: '1.5',
        title: 'Header cleanup, instruments & canvas density.',
        changes: [
          'Nested scale picker moves scale families into a dropdown so the header and canvas stay compact',
          'Adaptive pad density lets pads shrink visually as the canvas fills',
          'Instrument page is now available on iPhone',
          'Standalone ChordPond can discover and load installed AUv3 instruments into the 16-channel rack',
          'Bundled Frog Synth auto-loads into Channel 1 for new empty projects',
          'Loaded synth tiles show AU icons, open synth UI directly, and require armed eject before replacement',
          'AUv3 editors open in movable, resizable floating windows with close and maximize controls',
          'Review prompt moved to the Instruments page once the project-count threshold is met',
          'Closing the draw strip now disables draw mode so chord pads remain movable',
        ],
      },
      {
        version: '1.4',
        title: 'Ableton Link, swing investigation & AU restore.',
        changes: [
          'Ableton Link now syncs tempo, beat phase, and start/stop with a Link status pill and settings sheet',
          'Link controls live in the BPM/time strip on iPad and iPhone to keep the main header clear',
          'AUv3 host transport keeps precedence, with Link following in standalone mode',
          'Standalone start lands on the next Link bar boundary for quantized launch',
          'Link settings sheet includes an explicit Done button on compact iPhone',
          'Swing behavior was pinned with tests before changing it',
          'Offline export now matches live swing scheduling, groove swing participates, and 50% stays straight',
          'AU state restore now carries a full project snapshot for Logic, with UUID-only fallback for older AUM sessions',
          'Settings now include Support ChordPond, plus a conservative review prompt after a safe project-close milestone',
          'New motif patterns expand playback with Turn, Cascade, Orbit, Octave Echo, and Braid',
          'Standalone background playback can keep MIDI alive with a silent render path',
          'Compact canvas draw mode preserves scrolling lock, and iPad chord-circle swipes are locked while moving',
          'Scale Grid marks current chord tones explicitly and demotes the root-row highlight',
        ],
      },
      {
        version: '1.3',
        title: 'The one that removes random bugs and adds a random feature.',
        changes: [
          'Improvements: Restored Save As, project duplication, and per-chord probability controls',
          'Improvements: Added the RND chord pad for controlled harmonic variation',
          'Improvements: Improved AUv3 navigation in compact plugin windows',
          'Improvements: Added Mac scene navigation controls',
          'Improvements: Improved drawing workflow and scene editing',
          'Fixes: Fixed project deletion on the project list',
          'Fixes: Fixed scene swipe conflicts while drawing',
          'Fixes: Fixed scene undo crash',
        ],
      },
      {
        version: '1.2',
        title: 'The one that makes full arrangements.',
        changes: [
          'Added Scene Automation for building full song arrangements',
          'Added 15 new scales, including modal, exotic, and jazz scales',
          'Added sus2 and sus4 chord types',
          'Added 30-step canvas undo',
          'Added chord audition on tap',
          'Faster pad editing with direct drag-and-drop',
          'Added background audio playback with mix-with-others support',
          'Improved strum and note clipping behavior on shortened pads',
          'Strum voices now sustain correctly through the full chord length',
          'Fixed AUv3 MIDI output compatibility with Logic Pro for iPad',
        ],
      },
      {
        version: '1.1',
        title: 'The one with the initial fixes.',
        changes: [
          'Graceful Stop modes — Hard, End of Bar, End of Pattern',
          'Improved AUv3 reliability, restore behavior, and project duplication',
          'True “Save As…” support with safe project cloning',
          'Expanded strum timing controls (up to 600 ms)',
          'Per-note probability editing and per-chord probability bypass',
          'MIDI export with pattern-length aware defaults',
          'Optional solid-color background mode',
        ],
      },
      {
        version: '1.0',
        title: 'First public release.',
        changes: [
          'Visual chord-tile sequencer with paths and scenes',
          'Hold, Stutter, Roll/Arp, and Motif play modes',
          'Progression presets across multiple genres',
          'macOS support via Mac Catalyst (standalone + AUv3 in Logic Pro)',
          'AUv3 MIDI processor with sample-accurate output',
          'CoreMIDI',
          'Shared library between standalone and AU',
        ],
      },
    ],
  },
];
